import CharacterFactoryV2, { FightRole } from '../../components/Fight/CharacterFactoryV2';
import { skills } from '../../components/Fight/Skills/Skills';
import store from '../../store';
import { prepareMobileBattleAssets } from '../../util/mobileBattleAssets';
import { effects } from '../../components/Fight/Effects/Effects';

export function loadCharacterData(scene: Phaser.Scene, data: any) {
  const loadedSkills = new Set<number>();
  const loadedMotions = new Set<number>();
  const attemptedKeys = new Set<string>();

  if (process.env.NODE_ENV === 'development') {
    scene.load.crossOrigin = 'anonymous';
  }

  // timer = window.performance.now();

  // const loadingText = scene.add
  //   .text(scene.cameras.main.centerX, scene.cameras.main.centerY, 'Fetching actor data...', { fontFamily: 'Arial' })
  //   .setOrigin(0.5, 1);

  const controller=new AbortController();
  const cancelled=()=>controller.abort();
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN,cancelled);
  const loadIfAvailable=async(key:string,path:string):Promise<boolean>=>{
    if(controller.signal.aborted)return false;
    if(scene.cache.json.exists(key))return true;
    if(attemptedKeys.has(key))return false;
    attemptedKeys.add(key);
    try {
      const response=await fetch(path,{signal:controller.signal});
      if(!response.ok)return false;
      const value=await response.json();
      if(controller.signal.aborted)return false;
      scene.cache.json.add(key,value);
      return true;
    } catch {return false;}
  };
  const loadMotion = async (id: number) => {
    // loadingText.setText(`Loading motion data ${id}.`);
    const loaded = await loadIfAvailable(`skill-motion-${id}`, `${process.env.REACT_APP_CDN_PATH}skills/motion/${id}.json`);

    if (loaded) {
      loadedMotions.add(id);
    }

    return loaded;
  };

  const loadSkill = async (skillId: number) => {
    // loadingText.setText(`Loading skill data ${skillId}.`);
    const loaded = await loadIfAvailable(`skill-data-${skillId}`, `${process.env.REACT_APP_CDN_PATH}skills/data/${skillId}.json`);

    if (loaded) {
      loadedSkills.add(skillId);

      const skillJSON = scene.cache.json.get(`skill-data-${skillId}`);

      for (let i = 0; i < skillJSON.linked?.length ?? 0; i++) {
        await loadSkill(skillJSON.linked[i]);
      }

      for (let i = 0; i < skillJSON.motions?.length ?? 0; i++) {
        await loadMotion(skillJSON.motions[i]);
      }
    }

    return loaded;
  };

  return (async () => {
    // loadingText.setText('Loading default avatar.');

    if(!await loadIfAvailable(`avatar-data-1`, `${process.env.REACT_APP_CDN_PATH}actorsV2/people/1/data.json`))throw Error("Unable to load base character data");

    for (let i = 0; i < data.roles.length; i++) {
      const role = data.roles[i];

      // loadingText.setText(`Loading avatar ${role.avatar}.`);
      await loadIfAvailable(`avatar-data-${role.avatar}`, `${process.env.REACT_APP_CDN_PATH}actorsV2/people/${role.avatar}/data.json`);

      if (role.pet) {
        // loadingText.setText(`Loading pet`);
        await loadIfAvailable(`pet-data-${role.pet}`, `${process.env.REACT_APP_CDN_PATH}actorsV2/pets/${role.pet}/data.json`);
      }
    }

    // loadingText.setText(`Finished loading avatars.`);

    for (let i = 0; i < data.fight.length; i++) {
      const turn = data.fight[i];

      if (turn.isHit !== undefined && turn.isHit === false && !loadedSkills.has(7000)) {
        await loadSkill(7000);
      }

      if (turn.targetSkillId !== undefined) {
        if (!(turn.targetSkillId in skills) || !loadedSkills.has(turn.targetSkillId)) {
          await loadSkill(turn.targetSkillId);
        }
      }

      if (turn.skillId === undefined || turn.skillId === 1 || loadedSkills.has(turn.skillId) || turn.skillId in skills) {
        continue;
      }

      await loadSkill(turn.skillId);
    }

    // loadingText.setText(`Finished loading skills.`);

    // Preload font
    scene.add
      .text(-100, -100, 'load', {
        fontFamily: store.getState().settings.font,
        fontSize: 10,
      })
      .setVisible(false);
    const avatarData: { [key: string]: any } = {
      default: scene.cache.json.get('avatar-data-1'),
    };
    const petData = new Map<number, any>();

    avatarData[1] = scene.cache.json.get(`avatar-data-1`);
    data.roles.forEach((role: FightRole) => {
      avatarData[role.avatar] = scene.cache.json.get(`avatar-data-${role.avatar}`);

      if (role.pet) {
        petData.set(role.pet, scene.cache.json.get(`pet-data-${role.pet}`));
      }
    });

    const skillData = new Map<number, any>();
    const motionData = new Map<number, any>();

    loadedSkills.forEach((id) => skillData.set(id, scene.cache.json.get(`skill-data-${id}`)));
    loadedMotions.forEach((id) => motionData.set(id, scene.cache.json.get(`skill-motion-${id}`)));

    await loadIfAvailable('effect-offsets', `${process.env.REACT_APP_CDN_PATH}actors/data/effects/offsets.json`);

    CharacterFactoryV2.characterData = avatarData;
    CharacterFactoryV2.petData = petData;

    const atlasPaths=new Set<string>(['icons/people/all.json','icons/monsters/all.json','icons/effects.json','icons/skills.json',
      'actors/numbers/normal.json','actors/numbers/heal.json','skills/core/jutsu.json','skills/core/mud_wall.json','skills/core/substitution_technique.json']);
    for(const role of data.roles){
      const avatar=avatarData[role.avatar]??avatarData[1],name=avatar?.name??'people/1';
      for(const file of ['avatar.json','secret_technique.json',`${avatar?.secretTechnique?.concurrent??'secret_technique_1_1'}.json`,`weapons/${role.weaponId??1}.json`])atlasPaths.add(`actorsV2/${name}/${file}`);
      if(role.pet)atlasPaths.add(`actorsV2/pets/${role.pet}/avatar.json`);
    }
    skillData.forEach(skill=>atlasPaths.add(`skills/${skill.name}.json`));
    const visited=new Set<number>();
    const include=(id:number)=>{if(visited.has(id)||!skills[id])return;visited.add(id);atlasPaths.add(`skills/core/${skills[id].name}.json`);skills[id].linked?.forEach(include);};
    const offsets=scene.cache.json.get('effect-offsets')??{};
    for(const turn of data.fight){include(turn.skillId);include(turn.targetSkillId);for(const effect of turn.addEffect??[]){const name=offsets[effect.id]?String(effect.id):effects[effect.id]?.name;if(name)atlasPaths.add(`actors/effects/${name}.json`);}}
    const mobileAtlases=await prepareMobileBattleAssets(Array.from(atlasPaths));
    if(controller.signal.aborted)throw Error("Battle loading cancelled");
    return { ...data, avatarData, petData, skillData, motionData, mobileAtlases };
  })().finally(()=>scene.events.off(Phaser.Scenes.Events.SHUTDOWN,cancelled));
}
